import { StaticCanvas, FabricImage, Rect, Circle, Textbox, Gradient, Shadow } from 'fabric';
import { loadImageSafely, dataUrlToBlobSync } from './loadImageSafely';
import type { CollageLayout, CollageSlot } from './collageLayouts';
import type { CollageTheme } from './collageThemes';

const BASE_WIDTH = 1000;
const TEXT_SIZE_PX: Record<'s' | 'm' | 'l', number> = { s: 22, m: 36, l: 56 };

export interface CollageRenderItem {
  slotIndex: number;
  photoUrl: string;
  caption?: string | null;
  /** Crop focal point within the slot, 0-100 each — matches the editor's
   * CSS object-position preview exactly (50/50 = centered). */
  focalX?: number;
  focalY?: number;
}

export interface CollageRenderTextLayer {
  text: string;
  xPct: number;
  yPct: number;
  size: 's' | 'm' | 'l';
  color: string;
}

export interface RenderCollageParams {
  layout: CollageLayout;
  theme: CollageTheme;
  items: CollageRenderItem[];
  textLayers?: CollageRenderTextLayer[];
  title?: string | null;
  /** Base render width in px — height derives from the layout's own aspect ratio. */
  width?: number;
}

export interface RenderCollageResult {
  dataUrl: string;
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Composites a Grid-type collage into one flattened image via Fabric.js —
 * the actual replacement for the earlier hand-rolled canvas.drawImage
 * compositor. Every photo load goes through loadImageSafely() so R2's
 * missing CORS policy can't silently taint the canvas (see that file's
 * comment — this bug class cost multiple sessions to diagnose the first
 * time it was hit, via face detection).
 */
export async function renderCollageCanvas(params: RenderCollageParams): Promise<RenderCollageResult> {
  const { layout, theme, items, textLayers = [], title, width = BASE_WIDTH } = params;
  const [aspectW, aspectH] = layout.aspect.split('/').map(Number);
  const height = Math.round((width * aspectH) / aspectW);

  const canvasEl = document.createElement('canvas');
  const canvas = new StaticCanvas(canvasEl, { width, height, backgroundColor: theme.canvasBackground });

  // Filmstrip layout reserves real margin for sprocket rails — a property of
  // this *layout* (a reel shape with reserved margins), not of the theme.
  // Bolting sprockets onto an arbitrary theme/grid combo is what looked wrong
  // in the first pass at this feature.
  if (layout.id === 'filmstrip') {
    const railWidth = width * 0.065;
    drawSprocketRail(canvas, railWidth, 0, height);
    drawSprocketRail(canvas, railWidth, width - railWidth, height);
  }

  for (const item of items) {
    const slot = layout.slots[item.slotIndex];
    if (!slot) continue;
    await addPhotoToSlot(canvas, item, slot, theme, width, height);
  }

  if (title) {
    const bandHeight = height * 0.09;
    canvas.add(new Rect({
      left: 0, top: 0, width, height: bandHeight, originX: 'left', originY: 'top',
      fill: theme.dark ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.85)',
      selectable: false, evented: false,
    }));
    canvas.add(new Textbox(title, {
      left: width * 0.03, top: bandHeight * 0.22, width: width * 0.94, originX: 'left', originY: 'top',
      fontSize: Math.round(bandHeight * 0.42),
      fontWeight: 800,
      fontFamily: theme.captionFontFamily,
      fill: theme.dark ? '#ffffff' : '#1e1b3a',
      selectable: false, evented: false,
    }));
  }

  for (const layer of textLayers) {
    canvas.add(new Textbox(layer.text, {
      left: (layer.xPct / 100) * width,
      top: (layer.yPct / 100) * height,
      originX: 'center', originY: 'center',
      fontSize: TEXT_SIZE_PX[layer.size] * (width / BASE_WIDTH),
      fill: layer.color,
      fontFamily: theme.captionFontFamily,
      fontWeight: 700,
      textAlign: 'center',
      width: width * 0.8,
      selectable: false, evented: false,
    }));
  }

  canvas.renderAll();
  const dataUrl = canvas.toDataURL({ format: 'png', multiplier: 1 });
  const blob = dataUrlToBlobSync(dataUrl);
  return { dataUrl, blob, width, height };
}

async function addPhotoToSlot(
  canvas: StaticCanvas,
  item: CollageRenderItem,
  slot: CollageSlot,
  theme: CollageTheme,
  canvasW: number,
  canvasH: number
): Promise<void> {
  const sx = (slot.x / 100) * canvasW;
  const sy = (slot.y / 100) * canvasH;
  const sw = (slot.w / 100) * canvasW;
  const sh = (slot.h / 100) * canvasH;

  // Frame-specific inset — this is what makes each theme's structure feel
  // different, not just its color/filter (a real "print mat", real tape-
  // mount corners peeking past the edge, etc).
  const captionReserve = theme.frame === 'mat' && item.caption ? sh * 0.06 : 0;
  const inset = theme.frame === 'mat' ? sw * 0.02 : theme.frame === 'glow' ? sw * 0.015 : sw * 0.006;
  const ix = sx + inset;
  const iy = sy + inset;
  const iw = sw - inset * 2;
  const ih = sh - inset * 2 - captionReserve;

  const imgEl = await loadImageSafely(item.photoUrl);
  const img = new FabricImage(imgEl, { originX: 'left', originY: 'top', selectable: false, evented: false });
  const naturalW = img.width || imgEl.naturalWidth;
  const naturalH = img.height || imgEl.naturalHeight;
  const scale = Math.max(iw / naturalW, ih / naturalH);
  const dispW = naturalW * scale;
  const dispH = naturalH * scale;

  // Matches CSS object-position semantics exactly: 50/50 (the old fixed
  // behavior) centers the crop; 0/0 pins the image's top-left to the frame's
  // top-left (showing more of the image's top-left), 100/100 the opposite.
  const focalX = item.focalX ?? 50;
  const focalY = item.focalY ?? 50;
  img.set({
    left: ix - (dispW - iw) * (focalX / 100),
    top: iy - (dispH - ih) * (focalY / 100),
    scaleX: scale,
    scaleY: scale,
  });
  img.clipPath = new Rect({
    left: ix, top: iy, width: iw, height: ih, originX: 'left', originY: 'top',
    rx: theme.frame === 'glow' ? 18 : 0, ry: theme.frame === 'glow' ? 18 : 0,
    absolutePositioned: true,
  });

  const photoFilters = theme.buildPhotoFilters();
  if (photoFilters.length) {
    img.filters = photoFilters;
    img.applyFilters();
  }

  if (theme.frame === 'glow') {
    canvas.add(new Rect({
      left: sx - 4, top: sy - 4, width: sw + 8, height: sh + 8, originX: 'left', originY: 'top', rx: 18, ry: 18,
      fill: 'rgba(253,187,222,0.35)',
      shadow: new Shadow({ color: 'rgba(157,23,77,0.28)', blur: 20 }),
      selectable: false, evented: false,
    }));
  }

  canvas.add(img);

  if (theme.frame === 'tape') {
    canvas.add(makeTapeCorner(sx + 10, sy - 4, -9));
    canvas.add(makeTapeCorner(sx + sw - 36, sy + sh - 12, 7));
  }

  if (theme.frame === 'vignette') {
    const vignette = new Rect({ left: sx, top: sy, width: sw, height: sh, originX: 'left', originY: 'top', selectable: false, evented: false });
    vignette.set('fill', new Gradient({
      type: 'radial',
      coords: { x1: sw / 2, y1: sh / 2, r1: 0, x2: sw / 2, y2: sh / 2, r2: Math.max(sw, sh) / 1.3 },
      colorStops: [{ offset: 0, color: 'rgba(0,0,0,0)' }, { offset: 1, color: 'rgba(0,0,0,0.55)' }],
    }));
    canvas.add(vignette);
  }

  if (item.caption) {
    const capHeight = Math.max(sh * 0.12, 26);
    const capTop = theme.frame === 'mat' ? sy + sh - capHeight * 0.85 : sy + sh - capHeight;
    if (theme.captionBg !== 'transparent') {
      canvas.add(new Rect({ left: sx, top: capTop, width: sw, height: capHeight, originX: 'left', originY: 'top', fill: theme.captionBg, selectable: false, evented: false }));
    }
    canvas.add(new Textbox(item.caption, {
      left: sx + 8, top: capTop + 4, width: sw - 16, originX: 'left', originY: 'top',
      fontSize: Math.max(11, sh * 0.032),
      fontFamily: theme.captionFontFamily,
      fontWeight: theme.captionFontWeight,
      fontStyle: theme.captionFontStyle,
      fill: theme.captionColor,
      selectable: false, evented: false,
    }));
  }
}

function makeTapeCorner(cx: number, cy: number, angleDeg: number): Rect {
  return new Rect({
    left: cx, top: cy, width: 34, height: 16, originX: 'left', originY: 'top', angle: angleDeg,
    fill: 'rgba(255,255,255,0.6)',
    shadow: new Shadow({ color: 'rgba(0,0,0,0.18)', blur: 3, offsetY: 1 }),
    selectable: false, evented: false,
  });
}

function drawSprocketRail(canvas: StaticCanvas, railWidth: number, x: number, canvasH: number): void {
  canvas.add(new Rect({ left: x, top: 0, width: railWidth, height: canvasH, originX: 'left', originY: 'top', fill: '#0a0a0a', selectable: false, evented: false }));
  const holeR = railWidth * 0.22;
  const gap = holeR * 2.4;
  for (let cy = gap; cy < canvasH; cy += gap) {
    canvas.add(new Circle({
      left: x + railWidth / 2, top: cy, radius: holeR, originX: 'center', originY: 'center',
      fill: '#f2f2f2', selectable: false, evented: false,
    }));
  }
}

